/**
 * The application shell: one router, one theme control, and nothing else.
 *
 * Every page in this repo renders its own `data-surface="enterprise"` root,
 * its own skip link and its own chrome, because each was built to be mountable
 * on its own. This file does not wrap them in a second layout. It resolves the
 * route, hands the page its props, and stays out of the way.
 *
 * ── WHY THE THEME CONTROL IS FLOATING AND NOT IN A HEADER ────────────────
 * There are two headers on this surface: the gateway's `GatewayHeader` and the
 * dashboard's rail. The join screens deliberately have neither, because a
 * student meeting this page in an orientation session should see one column
 * and nothing else. Putting the control in a header would mean it is absent
 * on exactly the two screens whose dark rendering nobody has looked at yet.
 * One fixed control, marked `no-print`, appears on all seven routes.
 */

import { useEffect, useMemo } from "react";

import { AdminDashboard } from "~/app/pages/AdminDashboard";
import { AdminSettings } from "~/app/pages/AdminSettings";
import { AdminBilling } from "~/app/pages/AdminBilling";
import { AdminTeam } from "~/app/pages/AdminTeam";
import { AdminRoster } from "~/app/pages/AdminRoster";
import { AdminInvitations } from "~/app/pages/AdminInvitations";
import { AdminMatching } from "~/app/pages/AdminMatching";
import { Checkout } from "~/app/pages/Checkout";
import { CheckoutComplete } from "~/app/pages/CheckoutComplete";
import { Login } from "~/app/pages/Login";
import { Partner } from "~/app/pages/Partner";
import { Pricing } from "~/app/pages/Pricing";
import { Trust } from "~/app/pages/Trust";
import { Button, StateCard } from "~/app/components/primitives";
import { RequireSession } from "~/app/components/RequireSession";
import type { SessionPayload } from "~/app/lib/session";
import {
  KNOWN_PATHS,
  MOVED_PATHS,
  navigate,
  useHref,
  useLinkInterception,
  useRoute,
  useScrollRestoration,
  type Route,
} from "~/app/router";
import { trackPage } from "~/app/lib/analytics";

/* ==========================================================================
   Theme

   The page follows the operating system, and that is the whole feature.

   There used to be a three-state control pinned bottom-right. It was a review
   aid, it had been sitting on top of the capacity ladder's last scale mark at
   390px since it shipped, and a public marketing page has no business asking a
   visitor to pick a theme: their machine already answered. Removed Sep 8 2026.

   Sep 9 2026 (Thomas): the surface no longer follows the operating system
   either. It renders light until a toggle exists; the dark tokens stay in
   the stylesheets for that toggle, and index.html no longer sets the class
   before first paint. This hook only clears a class a cached shell might
   still carry and pins the UA colour scheme to light.
   ========================================================================== */

function useLightTheme(): void {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("dark");
    /* Keeps form controls, scrollbars and the canvas behind a short page in
       the same theme as the tokens. */
    root.style.setProperty("color-scheme", "light");
  }, []);
}

/* ==========================================================================
   /join and /join/:code: the member app's page now (Sep 10 2026)

   The claim is written by the student's own app with the student's own
   token (Architecture 5.2), and this host holds no student session, so the
   page that once rendered here could disclose but never write. The member
   web app has the page at platform.pulseofp3.org/join/:code (build order
   item 4); the poster host already sends students there in the Worker, and
   this host does the same for anyone who reaches it directly. The code and
   the `s` source parameter travel with the address. `Join.tsx` and
   `JoinDone.tsx` stay in the tree as the consent copy's reference.
   ========================================================================== */

const PLATFORM_JOIN_ORIGIN = "https://platform.pulseofp3.org";

export function platformJoinUrl(pathname: string, search: string): string {
  const parts = pathname.split("/").filter((s) => s.length > 0);
  const code = parts.length >= 2 && parts[1]!.toLowerCase() !== "done" ? `/${encodeURIComponent(parts[1]!)}` : "";
  return `${PLATFORM_JOIN_ORIGIN}/join${code}${search}`;
}

function JoinRedirect() {
  const href = useHref();
  const target = useMemo(() => {
    const at = href.search(/[?#]/);
    const pathname = at === -1 ? href : href.slice(0, at);
    const search = at === -1 ? "" : href.slice(at).replace(/#.*$/, "");
    return platformJoinUrl(pathname, search);
  }, [href]);

  useEffect(() => {
    window.location.replace(target);
  }, [target]);

  return (
    <div data-surface="enterprise" className="p3e-bare">
      <StateCard variant="loading" title="Opening the P3 app">
        Your school's join page is in the P3 app. If nothing happens,{" "}
        <a href={target}>open it here</a>.
      </StateCard>
    </div>
  );
}

/* ==========================================================================
   Not found
   ========================================================================== */

function NotFound({ pathname }: { pathname: string }) {
  return (
    <div data-surface="enterprise" className="p3e-bare">
      <StateCard variant="down" title="There is no page at this address">
        Nothing on this site answers to <code>{pathname}</code>. These are the
        pages that exist:
      </StateCard>
      <ul className="p3e-bare-links">
        {KNOWN_PATHS.map((entry) => (
          <li key={entry.path}>
            <a href={entry.path}>{entry.label}</a>
          </li>
        ))}
      </ul>
      <Button variant="ghost" size="sm" onClick={() => navigate("/pricing")}>
        Go to pricing
      </Button>
    </div>
  );
}

const BARE_CSS = `
.p3e-bare {
  min-height: 100vh;
  background: var(--canvas);
  color: var(--fg);
  font-family: var(--font-sans);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--sp-4);
  padding: var(--sp-12) var(--sp-4);
  max-width: 40rem;
  margin-inline: auto;
}
.p3e-bare code {
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 0.92em;
}
.p3e-bare-links { display: flex; flex-direction: column; gap: var(--sp-2); }
`;

/* ==========================================================================
   The routed surface
   ========================================================================== */

const DOCUMENT_TITLE: Record<Route["kind"], string> = {
  partner: "Career accelerator for your students | P3 Enterprise",
  pricing: "Pricing and registration | P3 Enterprise",
  checkout: "Register | P3 Enterprise",
  "checkout-complete": "Registration complete | P3 Enterprise",
  trust: "Security | P3 Enterprise",
  login: "Login | P3 Enterprise",
  admin: "Dashboard | P3 Enterprise",
  demo: "Sample dashboard | P3 Enterprise",
  "admin-settings": "Settings | P3 Enterprise",
  "demo-settings": "Settings, sample | P3 Enterprise",
  "admin-billing": "Billing | P3 Enterprise",
  "demo-billing": "Billing, sample | P3 Enterprise",
  "admin-team": "Team and roles | P3 Enterprise",
  "admin-roster": "Enrollment | P3 Enterprise",
  "admin-invitations": "Invitations | P3 Enterprise",
  "admin-matching": "Matching | P3 Enterprise",
  "demo-team": "Team and roles, sample | P3 Enterprise",
  "demo-roster": "Enrollment, sample | P3 Enterprise",
  "demo-invitations": "Invitations, sample | P3 Enterprise",
  "demo-matching": "Matching, sample | P3 Enterprise",
  join: "Join your institution on P3",
  "join-done": "You are connected | P3",
  "not-found": "Page not found | P3 Enterprise",
};

function Surface({ route }: { route: Route }) {
  switch (route.kind) {
    case "partner":
      return <Partner />;
    case "pricing":
      return <Pricing />;
    case "checkout":
      return <Checkout />;
    /* Where Stripe returns a buyer who paid (Sep 14 2026). Public, like
       /checkout: the buyer has no session yet, and the Worker answers by the
       Checkout Session id in the address, which only their browser holds. */
    case "checkout-complete":
      return <CheckoutComplete />;
    case "trust":
      return <Trust />;
    case "login":
      return <Login />;
    /* The signed-in host (Sep 9 2026): every /admin screen renders behind
       the session guard, which sends a signed-out visitor to /login and
       brings them back to the screen they asked for. /demo never passes
       through it: the sample dashboard is the signed-out shop window. */
    case "admin":
    case "admin-settings":
    case "admin-billing":
    case "admin-team":
    case "admin-roster":
    case "admin-invitations":
    case "admin-matching":
      return (
        <RequireSession>
          {(session) => <SignedIn route={route} session={session} />}
        </RequireSession>
      );
    case "demo":
      return <AdminDashboard demo />;
    case "demo-settings":
      return <AdminSettings basePath="/demo" demo />;
    case "demo-billing":
      return <AdminBilling basePath="/demo" demo />;
    case "demo-team":
      return <AdminTeam basePath="/demo" demo />;
    case "demo-roster":
      return <AdminRoster basePath="/demo" demo />;
    case "demo-invitations":
      return <AdminInvitations basePath="/demo" demo />;
    case "demo-matching":
      return <AdminMatching basePath="/demo" demo />;
    case "join":
    case "join-done":
      return <JoinRedirect />;
    case "not-found":
      return <NotFound pathname={route.pathname} />;
  }
}

function SignedIn({ route, session }: { route: Route; session: SessionPayload }) {
  switch (route.kind) {
    case "admin":
      return <AdminDashboard session={session} />;
    case "admin-settings":
      return <AdminSettings basePath="/admin" session={session} />;
    case "admin-billing":
      return <AdminBilling basePath="/admin" session={session} />;
    case "admin-team":
      return <AdminTeam basePath="/admin" session={session} />;
    case "admin-roster":
      return <AdminRoster basePath="/admin" session={session} />;
    case "admin-invitations":
      return <AdminInvitations basePath="/admin" session={session} />;
    case "admin-matching":
      return <AdminMatching basePath="/admin" session={session} />;
    default:
      return null;
  }
}

export function App() {
  const route = useRoute();
  useLightTheme();

  useLinkInterception();
  useScrollRestoration(route);

  // A moved path renders the page it moved to, then the address catches up,
  // so a link someone copies from the bar is the current one.
  const href = useHref();
  useEffect(() => {
    const at = href.search(/[?#]/);
    const pathname = at === -1 ? href : href.slice(0, at);
    const target = MOVED_PATHS[pathname];
    if (target) navigate(`${target}${at === -1 ? "" : href.slice(at)}`, { replace: true });
  }, [href]);

  useEffect(() => {
    document.title = DOCUMENT_TITLE[route.kind];
  }, [route.kind]);

  // Which pages a visitor opens (lib/analytics.ts): the page's name only.
  useEffect(() => {
    trackPage(route.kind);
  }, [route.kind]);

  return (
    <>
      <style>{BARE_CSS}</style>
      <Surface route={route} />
    </>
  );
}

export default App;
